import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../UserContext";
import toast from "react-hot-toast";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaBox,
  FaSearch,
  FaBarcode,
  FaMapMarkerAlt,
} from "react-icons/fa";
import "./style.css";

// Product Card Component - Matching StockProducts fields
const ProductCard = ({ product, onEdit, onDelete, isArabic, t }) => {
  const [showActions, setShowActions] = useState(false);

  const getDisplayName = () => {
    return isArabic
      ? product.nameAr || product.nameEn
      : product.nameEn || product.nameAr;
  };

  const getTypeName = () => {
    if (product.inventoryType) {
      return isArabic
        ? product.inventoryType.nameAr || product.inventoryType.nameEn
        : product.inventoryType.nameEn || product.inventoryType.nameAr;
    }
    return t("General");
  };

  const getLocationName = () => {
    if (product.inventoryLocation) {
      return isArabic
        ? product.inventoryLocation.nameAr || product.inventoryLocation.nameEn
        : product.inventoryLocation.nameEn || product.inventoryLocation.nameAr;
    }
    return "";
  };

  return (
    <div
      className="product-card"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className="product-card-header">
        <div className="product-icon-wrapper">
          <FaBox />
        </div>
        <div className={`product-actions ${showActions ? "visible" : ""}`}>
          <button
            className="action-btn edit-btn"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(product);
            }}
            title={t("Edit")}
          >
            <FaEdit />
          </button>
          <button
            className="action-btn delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(product);
            }}
            title={t("Delete")}
          >
            <FaTrash />
          </button>
        </div>
      </div>

      <div className="product-card-body">
        <div className="product-main-info">
          <h3 className="product-name" title={getDisplayName()}>
            {getDisplayName()}
          </h3>
          <span className="product-type-badge">{getTypeName()}</span>
        </div>

        <div className="product-details">
          {product.registrationNumber && (
            <div className="detail-item">
              <FaBarcode className="detail-icon" />
              <span className="detail-text">{product.registrationNumber}</span>
            </div>
          )}

          {getLocationName() && (
            <div className="detail-item">
              <FaMapMarkerAlt className="detail-icon" />
              <span className="detail-text">{getLocationName()}</span>
            </div>
          )}
        </div>

        <div className="product-balance-info-section">
          <div className="balance-info-label">
            {t("Current Stock")}
          </div>
          <div className="balance-info-display">
            <span className="balance-info-number">
              {product.currentBalance || 0}
            </span>
            <span className="balance-info-unit">{product.unit || t("unit")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  t,
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-container">
      <div className="modal-box">
        <h2 className="modal-title">{t("Confirm Deletion")}</h2>
        <p className="modal-confirmation-text">
          {t("Are you sure you want to delete")} <strong>"{itemName}"</strong>?{" "}
          {t("This action cannot be undone.")}
        </p>
        <div className="modal-buttons">
          <button className="button-secondary" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button className="button-danger" onClick={onConfirm}>
            {t("Delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Products Component
const ProductsApp = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { token, userData } = useContext(UserContext);

  // State management
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    product: null,
  });

  const searchInputRef = useRef(null);
  const farmId = userData.Employee?.farmId;

  // Fetch products - using same API as Android app and StockProducts component
  useEffect(() => {
    const fetchProducts = async () => {
      if (!farmId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(
          `api/inventoryItemDetails/getInventoryProductsWithCurrentBalance/${farmId}`,
          {
            headers: {
              Authorization: token,
            },
          }
        );

        // This endpoint returns products with current balance > 0
        const items = response.data?.items || [];

        setProducts(items);
        setFilteredProducts(items);
      } catch (error) {
        console.error("Error fetching products:", error);
        toast.error(t("Failed to load products."));
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [farmId, token, t]);

  // Search functionality - search only by product name
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProducts(products);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = products.filter((product) => {
      // Search only in product names (Arabic and English)
      const nameAr = (product.nameAr || "").toLowerCase();
      const nameEn = (product.nameEn || "").toLowerCase();

      return nameAr.includes(query) || nameEn.includes(query);
    });

    setFilteredProducts(filtered);
  }, [searchQuery, products]);

  // Handle edit
  const handleEdit = (product) => {
    navigate("/add-product", {
      state: {
        product,
        editMode: true,
      },
    });
  };

  // Handle delete
  const handleDeleteClick = (product) => {
    setDeleteModal({
      isOpen: true,
      product,
    });
  };

  const handleDeleteConfirm = async () => {
    const { product } = deleteModal;
    if (!product) return;

    try {
      await axios.delete(`api/inventoryItemDetails/${product.id}`, {
        headers: { Authorization: token },
      });

      toast.success(t("Product deleted successfully"));
      
      // Update local state
      const updatedProducts = products.filter((p) => p.id !== product.id);
      setProducts(updatedProducts);
      setFilteredProducts(updatedProducts);
      
      setDeleteModal({ isOpen: false, product: null });
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error(t("Failed to delete product"));
    }
  };

  // Handle add button click
  const handleAddProduct = () => {
    navigate("/add-product");
  };

  // Get display name for delete modal
  const getProductDisplayName = (product) => {
    if (!product) return "";
    return i18n.language === "ar"
      ? product.nameAr || product.nameEn
      : product.nameEn || product.nameAr;
  };

  return (
    <div className="products-container">
      {/* Header Section */}
      <header className="products-header">
        <div className="header-left">
          <h1 className="header-title">{t("Products")}</h1>
          <p className="header-subtitle">
            {filteredProducts.length} {t("products")} {t("available")}
          </p>
        </div>

        <div className="header-actions">
          <button className="button-primary" onClick={handleAddProduct}>
            <FaPlus /> {t("Add Product")}
          </button>
        </div>
      </header>

      {/* Controls Section */}
      <div className="controls-section">
        {/* Search Bar */}
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder={t("Search by name...")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Content Section */}
      <div className="content-section">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner">{t("Loading...")}</div>
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className="products-grid">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                isArabic={i18n.language === "ar"}
                t={t}
              />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <FaBox />
            </div>
            <h3>{t("No products found")}</h3>
            <p className="empty-message">
              {searchQuery
                ? t("No products match your search")
                : t("Add your first product to get started")}
            </p>
            {!searchQuery && (
              <button className="button-primary" onClick={handleAddProduct}>
                <FaPlus /> {t("Add Product")}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, product: null })}
        onConfirm={handleDeleteConfirm}
        itemName={getProductDisplayName(deleteModal.product)}
        t={t}
      />
    </div>
  );
};

export default ProductsApp;